// Plan 08-05 — Idempotency-Key generation + UUIDv4 validation.
//
// REQ-web-client: every POST /api/match/{id}/turn must carry an
// `Idempotency-Key` header (UUIDv4 format). Phase 3 backend FIFO-50 cache
// dedupes on this key — a transient retry with the SAME key returns the
// cached response; a fresh user re-submit (after a 4xx) generates a NEW key.
//
// The key is generated via `crypto.randomUUID()` which (per WHATWG spec)
// always produces a UUIDv4. Test-time determinism is gated by the
// `VITEST_DETERMINISTIC=1` env var inside `apps/web/test/setup.ts` (the same
// gate Plan 02 uses for nonce determinism).

/** UUIDv4 regex — version nibble = 4, variant nibble in {8,9,a,b}. */
export const UUID_V4_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/**
 * Create a fresh Idempotency-Key. Returns a UUIDv4 string sourced from the
 * Web Crypto API (`crypto.randomUUID()`), which is spec-required to be v4.
 *
 * Callers:
 *   - `submitTurn()` (this plan) — invoked once per turn submission.
 *   - On transient 5xx retry: caller MUST reuse the same key (RFC 9457
 *     `Idempotency-Key` semantics; Phase 3 FIFO-50 cache returns the
 *     original response on key-match).
 *   - On user-driven re-tap after a 4xx: caller MUST generate a new key
 *     (the prior submission failed; a fresh key signals a new attempt).
 */
export function createIdempotencyKey(): string {
  return crypto.randomUUID()
}

/**
 * True iff the given string is a well-formed UUIDv4. Useful at the API
 * boundary when accepting a caller-supplied key (e.g. cross-tab replay).
 */
export function isValidIdempotencyKey(k: string): boolean {
  if (typeof k !== 'string') return false
  return UUID_V4_RE.test(k)
}
